import React, { useState } from 'react';
import { Video, Play, Square, RefreshCw, Monitor, Settings, Sliders, Zap, Activity, Copy, Check } from '../ui/icons';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import ResponseDisplay from '../ui/ResponseDisplay';
import { liveUrlGameIndexingApi, imageMatchingApi, schedulerApi, ApiResponse, LastFrameResponse, TriggerResponse, ExecutionStatusResponse, InvalidateAudioUrlResponse } from '../../services/api';
import Colors, { withAlpha } from '../../constants/Colors';
import { copyToClipboard } from '../../utils/clipboard';

export default function LiveUrlGameIndexingPanel() {
  const audioUrlReferenceRows = [
    { team: 'WVU', url: 'https://img.leanstream.co/IM0301-MP3' },
    { team: 'UGA', url: 'https://img.leanstream.co/IM5001-MP3' },
    { team: 'Alabama', url: 'https://img.leanstream.co/IM7001-MP3' },
    { team: 'Syracuse', url: 'https://img.leanstream.co/IM4801-MP3' },
    { team: 'Duke', url: 'https://img.leanstream.co/IM3501-MP3' },
    { team: 'UNC', url: 'https://img.leanstream.co/IM9201-MP3' },
    { team: 'Cal', url: 'https://img.leanstream.co/LF1001-MP3' },
  ];

  // Trigger Now State (Master API)
  const [triggerGameName, setTriggerGameName] = useState('');
  const [triggerVideoUrl, setTriggerVideoUrl] = useState('');
  const [triggerGameType, setTriggerGameType] = useState('football');
  const [triggerVideoStartTimestamp, setTriggerVideoStartTimestamp] = useState('00:00:00');
  const [triggerCropHeightRatio, setTriggerCropHeightRatio] = useState('0.1');
  const [triggerFramesPerSecond, setTriggerFramesPerSecond] = useState('1');
  const [triggerManualSyncTimeGap, setTriggerManualSyncTimeGap] = useState('0');
  const [triggerAppType, setTriggerAppType] = useState<'dev' | 'prod'>('prod');
  const [triggerResponse, setTriggerResponse] = useState<ApiResponse<TriggerResponse> | null>(null);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [lastExecutionArn, setLastExecutionArn] = useState<string | null>(null);
  const [executionStatus, setExecutionStatus] = useState<ApiResponse<ExecutionStatusResponse> | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);

  // Setup State
  const [setupGameName, setSetupGameName] = useState('');
  const [setupFps, setSetupFps] = useState('1');
  const [setupResponse, setSetupResponse] = useState<ApiResponse | null>(null);
  const [setupLoading, setSetupLoading] = useState(false);

  // Start Indexing State
  const [indexVideoUrl, setIndexVideoUrl] = useState('');
  const [indexGameName, setIndexGameName] = useState('');
  const [indexVideoStartTimestamp, setIndexVideoStartTimestamp] = useState('00:00:00');
  const [indexGameType, setIndexGameType] = useState('football');
  const [indexCropRatio, setIndexCropRatio] = useState('0.1');
  const [startIndexingResponse, setStartIndexingResponse] = useState<ApiResponse | null>(null);
  const [startIndexingLoading, setStartIndexingLoading] = useState(false);

  // Last Frames State
  const [lastFramesGameName, setLastFramesGameName] = useState('');
  const [lastFramesResponse, setLastFramesResponse] = useState<ApiResponse<LastFrameResponse> | null>(null);
  const [lastFramesLoading, setLastFramesLoading] = useState(false);

  // Stop Processing State
  const [stopGameName, setStopGameName] = useState('');
  const [stopResponse, setStopResponse] = useState<ApiResponse | null>(null);
  const [stopLoading, setStopLoading] = useState(false);

  // Reset Game State
  const [resetGameName, setResetGameName] = useState('');
  const [resetFps, setResetFps] = useState('1');
  const [resetResponse, setResetResponse] = useState<ApiResponse | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  // Set Manual Sync Gap State
  const [syncGapGameName, setSyncGapGameName] = useState('');
  const [manualSyncTimeGap, setManualSyncTimeGap] = useState('');
  const [syncCalculationLatency, setSyncCalculationLatency] = useState('');
  const [syncGapTopK, setSyncGapTopK] = useState('');
  const [syncGapAppType, setSyncGapAppType] = useState<'dev' | 'prod'>('prod');
  const [syncGapResponse, setSyncGapResponse] = useState<ApiResponse | null>(null);
  const [syncGapLoading, setSyncGapLoading] = useState(false);

  // Invalidate Audio URL State
  const [audioUrlToInvalidate, setAudioUrlToInvalidate] = useState('');
  const [invalidateAudioLoading, setInvalidateAudioLoading] = useState(false);
  const [invalidateAudioResponse, setInvalidateAudioResponse] = useState<ApiResponse<InvalidateAudioUrlResponse> | null>(null);
  const [invalidateCooldownMessage, setInvalidateCooldownMessage] = useState<string | null>(null);
  const [copiedTeam, setCopiedTeam] = useState<string | null>(null);
  const [hoveredTeam, setHoveredTeam] = useState<string | null>(null);

  const handleCopyAudioUrl = async (team: string, url: string) => {
    const success = await copyToClipboard(url, '');
    if (success) {
      setCopiedTeam(team);
      setTimeout(() => setCopiedTeam((current) => (current === team ? null : current)), 1200);
    }
  };

  const handleTriggerNow = async () => {
    if (!triggerGameName || !triggerVideoUrl) return;

    setTriggerLoading(true);
    setTriggerResponse(null);
    setExecutionStatus(null);

    const response = await schedulerApi.triggerNow({
      game_name: triggerGameName,
      video_url: triggerVideoUrl,
      game_type: triggerGameType,
      video_start_timestamp: triggerVideoStartTimestamp || '00:00:00',
      crop_height_ratio: parseFloat(triggerCropHeightRatio) || 0.1,
      frames_per_second: parseFloat(triggerFramesPerSecond) || 1,
      manual_sync_time_gap: parseFloat(triggerManualSyncTimeGap) || 0,
      app_type: triggerAppType,
    });

    setTriggerResponse(response);
    setTriggerLoading(false);

    if (response.success && response.data?.execution_arn) {
      setLastExecutionArn(response.data.execution_arn);
    }
  };

  const handleCheckStatus = async () => {
    if (!lastExecutionArn) return;

    setStatusLoading(true);
    const response = await schedulerApi.getExecutionStatus(lastExecutionArn);
    setExecutionStatus(response);
    setStatusLoading(false);
  };

  const handleSetup = async () => {
    if (!setupGameName || !setupFps) return;

    setSetupLoading(true);
    const response = await liveUrlGameIndexingApi.setup({
      game_name: setupGameName,
      frames_per_second: parseFloat(setupFps),
    });
    setSetupResponse(response);
    setSetupLoading(false);
  };

  const handleStartIndexing = async () => {
    if (!indexVideoUrl || !indexGameName) return;

    setStartIndexingLoading(true);
    const response = await liveUrlGameIndexingApi.startIndexing({
      video_url: indexVideoUrl,
      game_name: indexGameName,
      video_start_timestamp: indexVideoStartTimestamp || undefined,
      game_type: indexGameType || undefined,
      crop_height_ratio: indexCropRatio ? parseFloat(indexCropRatio) : undefined,
    });
    setStartIndexingResponse(response);
    setStartIndexingLoading(false);
  };

  const handleGetLastFrames = async () => {
    if (!lastFramesGameName) return;

    setLastFramesLoading(true);
    const response = await liveUrlGameIndexingApi.getLastFrame(lastFramesGameName);
    setLastFramesResponse(response);
    setLastFramesLoading(false);
  };

  const handleStopProcessing = async () => {
    if (!stopGameName) return;

    setStopLoading(true);
    const response = await liveUrlGameIndexingApi.stopProcessing(stopGameName);
    setStopResponse(response);
    setStopLoading(false);
  };

  const handleResetGame = async () => {
    if (!resetGameName || !resetFps) return;

    const confirmReset = window.confirm(
      `Are you sure you want to reset "${resetGameName}"? This will drop the existing table and recreate it. Make sure you have stopped processing first.`
    );

    if (!confirmReset) return;

    setResetLoading(true);
    const response = await liveUrlGameIndexingApi.resetGame({
      game_name: resetGameName,
      frames_per_second: parseFloat(resetFps),
    });
    setResetResponse(response);
    setResetLoading(false);
  };

  const handleSetManualSyncGap = async () => {
    if (!syncGapGameName || !manualSyncTimeGap) return;

    setSyncGapLoading(true);
    const params: {
      game_name: string;
      manual_sync_time_gap: number;
      sync_calculation_latency?: number;
      top_k?: number;
      app_type?: 'dev' | 'prod';
    } = {
      game_name: syncGapGameName,
      manual_sync_time_gap: parseFloat(manualSyncTimeGap),
      app_type: syncGapAppType,
    };

    if (syncCalculationLatency) {
      params.sync_calculation_latency = parseFloat(syncCalculationLatency);
    }
    if (syncGapTopK) {
      params.top_k = parseInt(syncGapTopK, 10);
    }

    const response = await imageMatchingApi.setManualSyncGap(params);
    setSyncGapResponse(response);
    setSyncGapLoading(false);
  };

  const handleInvalidateAudioUrl = async () => {
    if (!audioUrlToInvalidate) return;

    setInvalidateAudioLoading(true);
    setInvalidateAudioResponse(null);
    setInvalidateCooldownMessage(null);
    const response = await liveUrlGameIndexingApi.invalidateAudioUrl(audioUrlToInvalidate.trim());
    if (!response.success && (response as any).cooldown) {
      setInvalidateCooldownMessage(response.error || 'Please wait before invoking again.');
    } else {
      setInvalidateAudioResponse(response);
    }
    setInvalidateAudioLoading(false);
  };

  return (
    <div className="section">
      <h2 className="section-title">
        <Video size={24} />
        Live URL Game Indexing
      </h2>

      {/* Master API - Trigger Step Functions Now */}
      <Card
        title="Start Game Indexing (Master API)"
        description="Directly triggers AWS Step Functions to setup and start indexing in one click"
        headerAction={<Zap size={20} style={{ color: '#FF9500' }} />}
      >
        <div className="form-grid-2">
          <Input
            label="Game Name"
            value={triggerGameName}
            onChange={setTriggerGameName}
            placeholder="e.g., nfl_chiefs_vs_ravens_2026"
            required
          />

          <Input
            label="Video URL"
            value={triggerVideoUrl}
            onChange={setTriggerVideoUrl}
            placeholder="https://stream-url.com/live"
            type="url"
            required
          />
        </div>

        <div className="form-grid-4" style={{ marginTop: '12px' }}>
          <div className="form-group">
            <label className="form-label">Game Type</label>
            <select
              className="form-input"
              value={triggerGameType}
              onChange={(e) => setTriggerGameType(e.target.value)}
            >
              <option value="football">football</option>
              <option value="basketball">basketball</option>
            </select>
          </div>
          <Input
            label="Video Start"
            value={triggerVideoStartTimestamp}
            onChange={setTriggerVideoStartTimestamp}
            placeholder="HH:MM:SS"
          />
          <Input
            label="Crop Ratio"
            value={triggerCropHeightRatio}
            onChange={setTriggerCropHeightRatio}
            placeholder="0.1"
            type="number"
          />
          <Input
            label="FPS"
            value={triggerFramesPerSecond}
            onChange={setTriggerFramesPerSecond}
            placeholder="1"
            type="number"
          />
        </div>

        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', marginTop: '12px' }}>
          <div style={{ width: '120px' }}>
            <Input
              label="Sync Gap (s)"
              value={triggerManualSyncTimeGap}
              onChange={setTriggerManualSyncTimeGap}
              placeholder="0"
              type="number"
            />
          </div>
          <div style={{ width: '120px' }}>
            <div className="form-group">
              <label className="form-label">App Type</label>
              <select
                className="form-input"
                value={triggerAppType}
                onChange={(e) => setTriggerAppType(e.target.value as 'dev' | 'prod')}
              >
                <option value="prod">prod</option>
                <option value="dev">dev</option>
              </select>
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <Button
            onClick={handleTriggerNow}
            loading={triggerLoading}
            disabled={!triggerGameName || !triggerVideoUrl}
            variant="warning"
            style={{ height: '44px' }}
          >
            <Zap size={18} />
            Start Indexing Now
          </Button>
        </div>

        {triggerResponse && (
          <div style={{ marginTop: '16px' }}>
            <ResponseDisplay
              response={triggerResponse.success ? triggerResponse.data : triggerResponse.error}
              error={!triggerResponse.success}
              label={triggerResponse.success ? 'Execution Started' : 'Error'}
            />

            {triggerResponse.success && lastExecutionArn && (
              <div style={{
                marginTop: '16px',
                padding: '16px',
                backgroundColor: 'rgba(88, 86, 214, 0.05)',
                borderRadius: '8px',
                border: '1px solid rgba(88, 86, 214, 0.2)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Activity size={16} style={{ color: '#5856D6' }} />
                    <span style={{ fontWeight: 600, fontSize: '14px', color: '#1C1C1E' }}>Execution Status</span>
                  </div>
                  <Button
                    onClick={handleCheckStatus}
                    loading={statusLoading}
                    variant="secondary"
                    style={{ padding: '6px 12px', fontSize: '12px' }}
                  >
                    <RefreshCw size={14} />
                    Check Status
                  </Button>
                </div>

                {executionStatus && (
                  <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: '6px',
                    padding: '12px',
                  }}>
                    {executionStatus.success && executionStatus.data ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', color: '#8E8E93', width: '80px' }}>Status:</span>
                          <span
                            style={{
                              padding: '4px 10px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 600,
                              backgroundColor:
                                executionStatus.data.status === 'SUCCEEDED' ? withAlpha(Colors.success, 0.15) :
                                executionStatus.data.status === 'RUNNING' ? 'rgba(88, 86, 214, 0.15)' :
                                executionStatus.data.status === 'FAILED' ? withAlpha(Colors.error, 0.15) :
                                'rgba(142, 142, 147, 0.15)',
                              color:
                                executionStatus.data.status === 'SUCCEEDED' ? Colors.success :
                                executionStatus.data.status === 'RUNNING' ? '#5856D6' :
                                executionStatus.data.status === 'FAILED' ? Colors.error :
                                '#8E8E93',
                            }}
                          >
                            {executionStatus.data.status}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '12px', color: '#8E8E93', width: '80px' }}>Started:</span>
                          <span style={{ fontSize: '12px', color: '#1C1C1E' }}>{executionStatus.data.start_date}</span>
                        </div>
                        {executionStatus.data.stop_date && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: '#8E8E93', width: '80px' }}>Stopped:</span>
                            <span style={{ fontSize: '12px', color: '#1C1C1E' }}>{executionStatus.data.stop_date}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ color: Colors.error, fontSize: '13px' }}>{executionStatus.error}</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="grid grid-2" style={{ marginTop: '24px' }}>
        {/* Setup Card */}
        <Card
          title="Game Setup"
          description="Create and index a new game - sets up all DBs, folders, etc."
          headerAction={<Settings size={20} style={{ color: '#8E8E93' }} />}
        >
          <Input
            label="Game Name"
            value={setupGameName}
            onChange={setSetupGameName}
            placeholder="e.g., nba_lakers_vs_celtics_2024"
            required
          />

          <Input
            label="Frames Per Second"
            value={setupFps}
            onChange={setSetupFps}
            placeholder="e.g., 1"
            type="number"
            required
          />

          <p style={{ fontSize: '12px', color: '#8E8E93', marginBottom: '16px' }}>
            Note: Only 1 frame per second will be processed for scoreboard analysis
          </p>

          <Button
            onClick={handleSetup}
            loading={setupLoading}
            disabled={!setupGameName || !setupFps}
            variant="success"
          >
            <Settings size={18} />
            Setup Game
          </Button>

          {setupResponse && (
            <ResponseDisplay
              response={setupResponse.success ? setupResponse.data : setupResponse.error}
              error={!setupResponse.success}
              label={setupResponse.success ? 'Setup Complete' : 'Error'}
            />
          )}
        </Card>

        {/* Start Indexing Card */}
        <Card
          title="Start Indexing"
          description="Start indexing a game after setup is done"
          headerAction={<Play size={20} style={{ color: Colors.success }} />}
        >
          <Input
            label="Video URL"
            value={indexVideoUrl}
            onChange={setIndexVideoUrl}
            placeholder="https://stream-url.com/live (HTTP, RTSP, RTMP)"
            type="url"
            required
          />

          <Input
            label="Game Name"
            value={indexGameName}
            onChange={setIndexGameName}
            placeholder="Must match a setup game"
            required
          />

          <Input
            label="Video Start Timestamp"
            value={indexVideoStartTimestamp}
            onChange={setIndexVideoStartTimestamp}
            placeholder="HH:MM:SS (default: 00:00:00)"
          />

          <div className="form-group">
            <label className="form-label">Game Type</label>
            <select
              className="form-input"
              value={indexGameType}
              onChange={(e) => setIndexGameType(e.target.value)}
            >
              <option value="football">football</option>
              <option value="basketball">basketball</option>
            </select>
          </div>

          <Input
            label="Crop Height Ratio"
            value={indexCropRatio}
            onChange={setIndexCropRatio}
            placeholder="0.0-1.0 (default: 0.1 = 10%)"
            type="number"
          />

          <Button
            onClick={handleStartIndexing}
            loading={startIndexingLoading}
            disabled={!indexVideoUrl || !indexGameName}
            variant="success"
          >
            <Play size={18} />
            Start Indexing
          </Button>

          {startIndexingResponse && (
            <ResponseDisplay
              response={startIndexingResponse.success ? startIndexingResponse.data : startIndexingResponse.error}
              error={!startIndexingResponse.success}
              label={startIndexingResponse.success ? 'Indexing Started' : 'Error'}
            />
          )}
        </Card>

        {/* Last Frames Card */}
        <Card
          title="Last Frames"
          description="Get last 10 frames details from the database"
          headerAction={<Monitor size={20} style={{ color: '#5856D6' }} />}
        >
          <Input
            label="Game Name"
            value={lastFramesGameName}
            onChange={setLastFramesGameName}
            placeholder="Enter game name"
            required
          />

          <Button
            onClick={handleGetLastFrames}
            loading={lastFramesLoading}
            disabled={!lastFramesGameName}
            variant="secondary"
          >
            <Monitor size={18} />
            Get Last Frames
          </Button>

          {lastFramesResponse && (
            <ResponseDisplay
              response={lastFramesResponse.success ? lastFramesResponse.data : lastFramesResponse.error}
              error={!lastFramesResponse.success}
              label={lastFramesResponse.success ? 'Frame Details' : 'Error'}
            />
          )}
        </Card>

        {/* Stop & Reset Card */}
        <Card
          title="Stop & Reset Processing"
          description="Stop processing or reset game indexing (drops table)"
        >
          <div className="alert alert-warning">
            <Square size={20} />
            <div>
              <strong>Warning:</strong> Reset will drop the existing table and recreate it. Always stop processing before resetting.
            </div>
          </div>

          <div className="form-grid-2">
            <div>
              <Input
                label="Game Name (Stop)"
                value={stopGameName}
                onChange={setStopGameName}
                placeholder="Enter game name"
                required
              />
              <Button
                onClick={handleStopProcessing}
                loading={stopLoading}
                disabled={!stopGameName}
                variant="warning"
              >
                <Square size={18} />
                Stop Processing
              </Button>
              {stopResponse && (
                <ResponseDisplay
                  response={stopResponse.success ? stopResponse.data : stopResponse.error}
                  error={!stopResponse.success}
                  label={stopResponse.success ? 'Stopped' : 'Error'}
                />
              )}
            </div>

            <div>
              <Input
                label="Game Name (Reset)"
                value={resetGameName}
                onChange={setResetGameName}
                placeholder="Enter game name"
                required
              />
              <Input
                label="Frames Per Second"
                value={resetFps}
                onChange={setResetFps}
                placeholder="e.g., 1"
                type="number"
                required
              />
              <Button
                onClick={handleResetGame}
                loading={resetLoading}
                disabled={!resetGameName || !resetFps}
                variant="danger"
              >
                <RefreshCw size={18} />
                Reset Game
              </Button>
              {resetResponse && (
                <ResponseDisplay
                  response={resetResponse.success ? resetResponse.data : resetResponse.error}
                  error={!resetResponse.success}
                  label={resetResponse.success ? 'Reset Complete' : 'Error'}
                />
              )}
            </div>
          </div>
        </Card>

        {/* Set Manual Sync Gap Card */}
        <Card
          title="Set Manual Sync Gap"
          description="Set manual sync time gap for a specific game"
          headerAction={<Sliders size={20} style={{ color: '#5856D6' }} />}
        >
          <Input
            label="Game Name"
            value={syncGapGameName}
            onChange={setSyncGapGameName}
            placeholder="e.g., nba_lakers_vs_celtics"
            required
          />

          <Input
            label="Manual Sync Time Gap (seconds)"
            value={manualSyncTimeGap}
            onChange={setManualSyncTimeGap}
            placeholder="e.g., 9.0"
            type="number"
            required
          />

          <Input
            label="Sync Calculation Latency"
            value={syncCalculationLatency}
            onChange={setSyncCalculationLatency}
            placeholder="Optional: latency in seconds"
            type="number"
          />

          <Input
            label="Top K"
            value={syncGapTopK}
            onChange={setSyncGapTopK}
            placeholder="Optional: number of top results"
            type="number"
          />

          <div className="form-group">
            <label className="form-label">App Type</label>
            <select
              className="form-input"
              value={syncGapAppType}
              onChange={(e) => setSyncGapAppType(e.target.value as 'dev' | 'prod')}
            >
              <option value="prod">prod</option>
              <option value="dev">dev</option>
            </select>
          </div>

          <Button
            onClick={handleSetManualSyncGap}
            loading={syncGapLoading}
            disabled={!syncGapGameName || !manualSyncTimeGap}
            variant="secondary"
          >
            <Sliders size={18} />
            Set Sync Gap
          </Button>

          {syncGapResponse && (
            <ResponseDisplay
              response={syncGapResponse.success ? syncGapResponse.data : syncGapResponse.error}
              error={!syncGapResponse.success}
              label={syncGapResponse.success ? 'Sync Gap Set' : 'Error'}
            />
          )}
        </Card>

        <Card
          title="Update Audio URL"
          description={
            <>
              Invalidates cache and updates this{' '}
              <a
                href="https://db863p6fnrzwe.cloudfront.net/live/playlist.m3u8"
                target="_blank"
                rel="noreferrer"
                style={{ color: '#FFFFFF' }}
              >
                url
              </a>
              , To the new source url below :
            </>
          }
          headerAction={<RefreshCw size={20} style={{ color: '#FF9500' }} />}
        >
          <Input
            label="Audio URL"
            value={audioUrlToInvalidate}
            onChange={setAudioUrlToInvalidate}
            placeholder="https://img.leanstream.co/IM9201-MP3"
            type="url"
            required
          />

          <Button
            onClick={handleInvalidateAudioUrl}
            loading={invalidateAudioLoading}
            disabled={!audioUrlToInvalidate}
            variant="warning"
          >
            <RefreshCw size={18} />
            Update Audio URL
          </Button>

          {invalidateAudioLoading && (
            <p style={{ fontSize: '12px', color: '#8E8E93', marginTop: '10px' }}>
              Processing audio updation, waiting for API response...
            </p>
          )}

          {invalidateCooldownMessage && (
            <div className="response-container" style={{ marginBottom: '12px', borderColor: 'rgba(255,255,255,0.25)', color: '#DCE7FF' }}>
              <div className="response-label" style={{ color: '#DCE7FF' }}>Info</div>
              <div className="response-content" style={{ color: '#DCE7FF' }}>{invalidateCooldownMessage}</div>
            </div>
          )}

          {invalidateAudioResponse && (
            <ResponseDisplay
              response={invalidateAudioResponse.success ? invalidateAudioResponse.data : invalidateAudioResponse.error}
              error={!invalidateAudioResponse.success}
              label={invalidateAudioResponse.success ? 'Audio URL Updated' : 'Error'}
            />
          )}

          <div style={{ marginTop: '12px' }}>
            <p style={{ fontSize: '12px', color: '#8E8E93', marginBottom: '8px', fontWeight: 700 }}>
              Team Audio URL Reference
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {audioUrlReferenceRows.map((row) => (
                <div
                  key={row.team}
                  onMouseEnter={() => setHoveredTeam(row.team)}
                  onMouseLeave={() => setHoveredTeam((current) => (current === row.team ? null : current))}
                  className="audio-ref-row"
                  style={{
                    padding: '10px 12px',
                    borderRadius: '12px',
                    border: hoveredTeam === row.team ? '1px solid rgba(188, 206, 255, 0.42)' : '1px solid rgba(171, 191, 255, 0.28)',
                    background:
                      hoveredTeam === row.team
                        ? 'linear-gradient(145deg, rgba(72, 103, 205, 0.4), rgba(44, 69, 156, 0.32))'
                        : 'linear-gradient(145deg, rgba(53, 79, 171, 0.32), rgba(31, 53, 130, 0.24))',
                    boxShadow:
                      hoveredTeam === row.team
                        ? '0 14px 24px rgba(8, 16, 48, 0.35), inset 0 1px 0 rgba(220, 232, 255, 0.22)'
                        : '0 8px 18px rgba(8, 16, 48, 0.24), inset 0 1px 0 rgba(204, 220, 255, 0.18)',
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    transform: hoveredTeam === row.team ? 'translateY(-2px) scale(1.01)' : 'translateY(0) scale(1)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease',
                  }}
                >
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#D6E2FF', letterSpacing: '0.3px' }}>{row.team}</span>
                  <a
                    href={row.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: '12px', color: '#FFFFFF', textDecoration: 'underline', wordBreak: 'break-all' }}
                  >
                    {row.url}
                  </a>
                  <button
                    type="button"
                    title={copiedTeam === row.team ? 'Copied' : 'Copy URL'}
                    onClick={() => handleCopyAudioUrl(row.team, row.url)}
                    style={{
                      width: '26px',
                      height: '26px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '8px',
                      border: copiedTeam === row.team ? '1px solid rgba(110, 255, 193, 0.45)' : '1px solid rgba(200, 214, 255, 0.3)',
                      background: copiedTeam === row.team ? 'rgba(70, 224, 154, 0.22)' : 'rgba(255, 255, 255, 0.08)',
                      color: copiedTeam === row.team ? '#8DFFD3' : '#DCE7FF',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    {copiedTeam === row.team ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
